import { equals, isEmpty, reject } from 'ramda';

import { ChoiceListItem, Typography, tk } from '@sunwebgroup/ui-components';
import { styled } from 'styled-components';
import type { CheckedChangedEventHandler, IconGlyph } from '@sunwebgroup/ui-components';

import { useMultiSelectFilter } from '../../../../domain/hooks';
import { useIntl } from '@/intl';
import FilterGroup from './FilterGroup';
import { ShowMore } from '../../../../../../common/components/ShowMore';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createSearchFilterClickEvent } from '../../../../domain/analytics';

const iconMap = new Map<string, IconGlyph>([
  // facilities
  ['LG', 'IconBed'],
  ['BAR', 'IconCocktail'],
  ['FITNESS', 'IconDumbbell'],
  ['HAMMAM', 'IconHamam'],
  ['INTERNET_PUBLIC_SPACE', 'IconWifi'],
  ['JACUZZI', 'IconSwimmingPool'],
  ['PRIVATE_POOL', 'IconSwimmingPool'],
  ['SWIMMING_POOL', 'IconSwimmingPool'],
  ['SAFE', 'IconLock'],
  ['LIFT', 'IconElevator'],
  ['MASSAGE', 'IconTowel'],
  ['ROOM_DISABLED_PEOPLE', 'IconWheelchair'],
  ['FACILITIES_DISABLED_PEOPLE', 'IconWheelchair'],
  ['PARKING_GARAGE', 'IconParking'],
  ['RECEPTION', 'IconReception'],
  ['RESTAURANT', 'IconCutlery'],
  ['ROOM_SERVICE', 'IconRoomservice'],
  ['SAUNA', 'IconSauna'],
  ['DUTCH_TV_CHANNELS', 'IconTv'],
  ['PUBLIC_SPACE_TV', 'IconTv'],
  ['LAUNDRY', 'IconWashingMachine'],
  ['SKI_STORAGE', 'IconSkiMaterial'],
  ['TENNIS_COURT', 'IconTennisball'],

  // children facilities
  ['CHILDREN_CHAIR', 'IconChildSeatHighChair'],
  ['CHILDREN_PLAYING_AREA', 'IconChildrenPlaySpace'],
  ['CHILDREN_SWIMMING_POOL', 'IconBabyPool'],
  ['ENTERTAINMENT_FOR_CHILDREN', 'IconClown'],
  ['SWIMMING_POOL_WITH_WATERSLIDE', 'IconPoolSlide'],

  // internet facilities
  ['FREE_WIFI', 'IconWifi'],
  ['PAID_WIFI', 'IconWifi'],
]);

const getIconFromValue = (value: string): IconGlyph => {
  const icon = iconMap.get(value);

  return icon || 'IconFilter';
};

export function FacilitiesFilter() {
  const intl = useIntl();
  const { sendEvent } = useAnalytics();
  const { isFetching, options, selectedValues, setSelection } = useMultiSelectFilter('Facilities');
  const {
    isFetching: isFetchingChildren,
    options: childrenOptions,
    selectedValues: selectedChildrenValues,
    setSelection: setChildrenSelection,
  } = useMultiSelectFilter('ChildrenFacilities');
  const {
    isFetching: isFetchingInternet,
    options: internetOptions,
    selectedValues: selectedInternetValues,
    setSelection: setInternetSelection,
  } = useMultiSelectFilter('InternetFacilities');

  const handleFilterChange: CheckedChangedEventHandler = ({ checked, value }) => {
    if (checked) {
      setSelection([...selectedValues, value]);
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'general facility',
          result: [...selectedValues, value].join(','),
        })
      );
    }

    if (!checked) {
      setSelection(reject(equals(value), selectedValues));
    }
  };

  const handleChildrenFilterChange: CheckedChangedEventHandler = ({ checked, value }) => {
    if (checked) {
      setChildrenSelection([...selectedChildrenValues, value]);
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'children facility',
          result: [...selectedChildrenValues, value].join(','),
        })
      );
    }

    if (!checked) {
      setChildrenSelection(reject(equals(value), selectedChildrenValues));
    }
  };

  const handleInternetFilterChange: CheckedChangedEventHandler = ({ checked, value }) => {
    if (checked) {
      setInternetSelection([...selectedInternetValues, value]);
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'internet facility',
          result: [...selectedInternetValues, value].join(','),
        })
      );
    }

    if (!checked) {
      setInternetSelection(reject(equals(value), selectedInternetValues));
    }
  };

  if (isEmpty(options) && isEmpty(childrenOptions) && isEmpty(internetOptions)) return null;

  return (
    <FilterGroup
      title={intl.formatMessage({ id: 'facilities_label' })}
      defaultOpen={selectedValues.length > 0 || selectedChildrenValues.length > 0 || selectedInternetValues.length > 0}
    >
      {options.length > 0 && (
        <Fieldset>
          <StyledCategoryLegend>
            {intl.formatMessage({ id: 'facilities_category_label_facilities' })}
          </StyledCategoryLegend>
          <ShowMore
            openLabel={intl.formatMessage({ id: 'show_more' })}
            closeLabel={intl.formatMessage({ id: 'show_less' })}
          >
            {options.map(({ name, value, resultsCount }) => (
              <ChoiceListItem
                choiceListItemType="checkbox"
                key={name}
                value={value}
                checked={selectedValues.includes(value)}
                label={name}
                quantity={resultsCount}
                icon={getIconFromValue(value)}
                onCheckedChange={handleFilterChange}
                disabled={isFetching}
              />
            ))}
          </ShowMore>
        </Fieldset>
      )}

      {childrenOptions.length > 0 && (
        <Fieldset>
          <StyledCategoryLegend>
            {intl.formatMessage({ id: 'facilities_category_label_children_facilities' })}
          </StyledCategoryLegend>
          <ShowMore
            openLabel={intl.formatMessage({ id: 'show_more' })}
            closeLabel={intl.formatMessage({ id: 'show_less' })}
          >
            {childrenOptions.map(({ name, value, resultsCount }) => (
              <ChoiceListItem
                choiceListItemType="checkbox"
                key={name}
                value={value}
                checked={selectedChildrenValues.includes(value)}
                label={name}
                quantity={resultsCount}
                icon={getIconFromValue(value)}
                onCheckedChange={handleChildrenFilterChange}
                disabled={isFetchingChildren}
              />
            ))}
          </ShowMore>
        </Fieldset>
      )}

      {internetOptions.length > 0 && (
        <Fieldset>
          <StyledCategoryLegend>
            {intl.formatMessage({ id: 'facilities_category_label_internet_facilities' })}
          </StyledCategoryLegend>
          <ShowMore
            openLabel={intl.formatMessage({ id: 'show_more' })}
            closeLabel={intl.formatMessage({ id: 'show_less' })}
          >
            {internetOptions.map(({ name, value, resultsCount }) => (
              <ChoiceListItem
                choiceListItemType="checkbox"
                key={name}
                value={value}
                checked={selectedInternetValues.includes(value)}
                label={name}
                quantity={resultsCount}
                icon={getIconFromValue(value)}
                onCheckedChange={handleInternetFilterChange}
                disabled={isFetchingInternet}
              />
            ))}
          </ShowMore>
        </Fieldset>
      )}
    </FilterGroup>
  );
}

const StyledCategoryLegend = styled(Typography).attrs({ $variant: 'body-md', as: 'legend' })`
  color: ${tk('color_semantic_text')};
  margin-top: ${tk('spacing_xs')};
  margin-bottom: ${tk('spacing_xs')};
`;

const Fieldset = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
`;
